import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Circle, CircleDot, Loader2, Search } from "lucide-react";
import { 
  IA_SUBCATEGORIES, 
  getSubcategoryIdFromActivityId, 
  getSubcategoryName
} from "@/lib/ia-data-utils";
import type { IASelection, IARequirements } from "@/lib/ia-calculator-utils";

interface IAPracticeInfo {
  isSmallPractice: boolean;
  isHPSAOrRural: boolean;
  isNonPatientFacing: boolean;
}

interface IAMeasure {
  measureId: string;
  title: string;
  description: string;
}

interface IAConfigurationTabProps {
  iaWeight: number;
  useModeledIa: boolean;
  modeledIaPercent: number | undefined;
  practiceInfo: IAPracticeInfo;
  iaRequirements: IARequirements | null;
  iaSelections: IASelection[];
  iaSearchQuery: string;
  iaSubcategory: string;
  filteredIaMeasures: IAMeasure[];
  iaMeasures: IAMeasure[];
  iaSavingSelection: string | null;
  onUseModeledChange: (useModeled: boolean) => void;
  onModeledPercentChange: (percent: number | undefined) => void;
  onSearchQueryChange: (query: string) => void;
  onSubcategoryChange: (subcategory: string) => void;
  onToggleSelection: (measureId: string) => void;
}

export function IAConfigurationTab({
  iaWeight,
  useModeledIa,
  modeledIaPercent,
  practiceInfo,
  iaRequirements,
  iaSelections,
  iaSearchQuery,
  iaSubcategory,
  filteredIaMeasures,
  iaMeasures,
  iaSavingSelection,
  onUseModeledChange,
  onModeledPercentChange,
  onSearchQueryChange,
  onSubcategoryChange,
  onToggleSelection
}: IAConfigurationTabProps) {
  // Always honor the current practice flags first; fall back to server-provided requirements
  const isSpecialStatus = practiceInfo.isSmallPractice || practiceInfo.isHPSAOrRural || practiceInfo.isNonPatientFacing;
  const activitiesRequired = isSpecialStatus
    ? 1
    : iaRequirements?.activitiesRequired || 2;
  const activitiesSelected = iaSelections.length;
  
  // When IA is reweighted to 0, show percentage out of 100 instead of weighted score
  const isReweighted = iaWeight === 0;
  const maxPoints = isReweighted ? 100 : iaWeight;
  
  // Calculate scores
  const calculatedScore = isReweighted 
    ? Math.min(100, (activitiesSelected / activitiesRequired) * 100)
    : Math.min(iaWeight, (activitiesSelected / activitiesRequired) * iaWeight);
  const estimatedScore = modeledIaPercent !== undefined 
    ? (isReweighted ? modeledIaPercent : (modeledIaPercent * iaWeight / 100))
    : 0;
  const currentScore = useModeledIa ? estimatedScore : calculatedScore;
  const progressPercent = useModeledIa 
    ? (modeledIaPercent || 0) 
    : Math.min(100, (activitiesSelected / activitiesRequired) * 100);
  
  // Practice type for message
  const practiceType = practiceInfo.isSmallPractice ? 'small practice' 
    : practiceInfo.isHPSAOrRural ? 'rural/HPSA practice'
    : practiceInfo.isNonPatientFacing ? 'non-patient facing practice'
    : 'large practice';

  // Circle dimensions for SVG
  const strokeWidth = 5;

  // Circular progress component
  const CircularProgress = ({ sz = 100 }: { sz?: number }) => {
    const r = (sz - strokeWidth) / 2;
    const c = 2 * Math.PI * r;
    const offset = c - (progressPercent / 100) * c;
    return (
      <div className="relative" style={{ width: sz, height: sz }}>
        <svg className="transform -rotate-90" width={sz} height={sz}>
          <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
          <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke="hsl(var(--primary))" strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} className="transition-all duration-500" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold text-gray-900">{Math.round(currentScore)}</span>
          <span className="text-xs text-gray-500">of {maxPoints}{isReweighted ? '%' : ''}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* IA Score Preview */}
      <Card>
        <CardContent className="py-5 px-6">
          <div className="flex items-center">
            <div className="flex flex-col pr-6 border-r border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Score Method</h4>
              <div className="space-y-2">
                <button 
                  onClick={() => onUseModeledChange(false)} 
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-all w-full ${!useModeledIa ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  {!useModeledIa ? <CircleDot className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  Calculate from IA data
                </button>
                <button 
                  onClick={() => onUseModeledChange(true)} 
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-all w-full ${useModeledIa ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  {useModeledIa ? <CircleDot className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  Enter estimate
                </button>
                {useModeledIa && (
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number" 
                      min="0" 
                      max="100" 
                      placeholder="0-100" 
                      value={modeledIaPercent || ''} 
                      onChange={(e) => onModeledPercentChange(e.target.value ? parseFloat(e.target.value) : undefined)} 
                      className="w-[92px] h-9" 
                    />
                    <span className="text-sm text-muted-foreground">% expected IA score</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 px-6">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                IA Score Preview
              </h3>
              <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                Improvement Activities contribute <strong className="text-gray-900">{iaWeight}%</strong> to your final MIPS score
                {isReweighted && <span className="text-gray-500"> (reweighted)</span>}
              </p>
              <p className="text-sm text-amber-800 mt-2.5 leading-relaxed">
                As a <strong>{practiceType}</strong>, you only need <strong>{activitiesRequired} {activitiesRequired === 1 ? 'activity' : 'activities'}</strong> for full credit.{' '}
                {activitiesSelected < activitiesRequired && 'Select an activity below to earn 40 points.'}
              </p>
            </div>
            <div className="flex-shrink-0 pl-6 border-l border-gray-200">
              {/* Show dash when reweighted, otherwise show activities counter or circular progress */}
              {isReweighted ? (
                <div className="text-center min-w-[100px]">
                  <div className="text-4xl font-bold text-gray-400">—</div>
                  <div className="text-sm text-gray-500 mt-1">Reweighted</div>
                </div>
              ) : !useModeledIa ? (
                <div className="text-center min-w-[100px]">
                  <div className="text-4xl font-bold text-gray-900 tabular-nums">
                    {activitiesSelected}<span className="text-2xl text-gray-400 font-normal">/{activitiesRequired}</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">Activities Selected</div>
                </div>
              ) : (
                <CircularProgress />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Select Activities */}
      <Card className="bg-gray-50/50">
        <CardHeader className="pb-3">
          <CardTitle>Select Activities</CardTitle>
          <CardDescription>Choose improvement activities for your practice</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input - increased by 20% from 0.8 to 0.96 (0.8 * 1.2) */}
            <div className="relative flex-[0.96]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by ID, title, or description..."
                value={iaSearchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                className="pl-9 bg-white"
              />
            </div>
            
            {/* Subcategory Dropdown */}
            <Select value={iaSubcategory} onValueChange={onSubcategoryChange}>
              <SelectTrigger className="w-full sm:w-[280px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                {IA_SUBCATEGORIES.map((subcat) => (
                  <SelectItem key={subcat.id} value={subcat.id}>
                    <span>{subcat.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Results Count */}
            <Badge variant="secondary" className="self-center whitespace-nowrap">
              {filteredIaMeasures.length} of {iaMeasures.length} activities
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Activity Grid */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filteredIaMeasures.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-muted/30 rounded-lg border border-dashed">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-muted-foreground">No activities found matching your criteria.</p>
            {(iaSearchQuery || iaSubcategory !== 'all') && (
              <Button 
                variant="link" 
                onClick={() => { onSearchQueryChange(''); onSubcategoryChange('all'); }}
              >
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          filteredIaMeasures.map(measure => {
            const subcatId = getSubcategoryIdFromActivityId(measure.measureId);
            const isSelected = iaSelections.some(s => s.ia_activity_id === measure.measureId);
            const isSaving = iaSavingSelection === measure.measureId;
            
            return (
              <Card key={measure.measureId} className={`h-72 flex flex-col ${isSelected ? 'ring-2 ring-primary/40 bg-primary/5' : ''}`}>
                <CardHeader className="pb-2 flex-none">
                  <div className="flex items-center justify-between gap-4">
                    <CardTitle className="text-base">{measure.measureId}</CardTitle>
                    {subcatId && (
                      <span className={`text-xs font-medium uppercase tracking-wide whitespace-nowrap ${
                        subcatId === 'AHE' ? 'text-purple-600' :
                        subcatId === 'BE' ? 'text-blue-600' :
                        subcatId === 'CC' ? 'text-teal-600' :
                        subcatId === 'EPA' ? 'text-green-600' :
                        subcatId === 'PM' ? 'text-orange-600' :
                        subcatId === 'PSPA' ? 'text-red-600' :
                        subcatId === 'BMH' ? 'text-indigo-600' :
                        'text-gray-600'
                      }`}>
                        {getSubcategoryName(subcatId)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium line-clamp-2 mt-1">{measure.title}</p>
                </CardHeader>
                <div className="border-t mx-6 flex-none" />
                <CardContent className="pt-3 flex-1 overflow-hidden">
                  <p className="text-sm text-gray-600 line-clamp-4">
                    {measure.description}
                  </p>
                </CardContent>
                <CardContent className="pt-0 flex-none">
                  <Button 
                    variant={isSelected ? "default" : "secondary"} 
                    className="w-full"
                    onClick={() => onToggleSelection(measure.measureId)}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                    ) : isSelected ? (
                      "Selected ✓"
                    ) : (
                      "Select Activity"
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
